/** Bounded observation only. Thresholds request evidence; they never change the simulation. */
export const INCIDENT_CODES = ['window_error', 'unhandled_rejection', 'worker_error', 'engine_stopped', 'storage_error', 'network_error', 'world_check_failed', 'gdp_check_failed', 'replay_mismatch', 'archive_blocked', 'upload_backlog', 'receipt_backlog', 'suspected_interruption', 'resource_error', 'population_drop', 'price_jump', 'employment_drop', 'homeless_jump', 'treasury_drop', 'food_supply_warning', 'material_supply_warning', 'wage_arrears', 'maintenance_risk'] as const;
export const MONITOR_PHASES = ['startup', 'step', 'render', 'export', 'storage', 'network', 'unknown'] as const;
export type IncidentCode = typeof INCIDENT_CODES[number];
export type Incident = { code: IncidentCode; severity: 'info' | 'warning' | 'error'; phase: typeof MONITOR_PHASES[number]; count: number; observed?: number; reference?: number };
export type Lifecycle = { state: 'start' | 'pause' | 'resume' | 'heartbeat' | 'close'; reason?: 'user' | 'background' | 'error' | 'restore' | 'new_city' | 'unknown'; visibility?: 'visible' | 'hidden'; uptimeMs?: number };
const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && Math.abs(n) <= Number.MAX_SAFE_INTEGER;
const object = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
const uint = (x: unknown): x is number => typeof x === 'number' && Number.isSafeInteger(x) && x >= 0 && x <= 0xffffffff;
export function validIncident(x: unknown): x is Incident {
  return object(x) && Object.keys(x).every(k => ['code', 'severity', 'phase', 'count', 'observed', 'reference'].includes(k)) &&
    INCIDENT_CODES.includes(x.code as IncidentCode) && ['info', 'warning', 'error'].includes(String(x.severity)) && MONITOR_PHASES.includes(x.phase as Incident['phase']) && uint(x.count) && x.count > 0 &&
    (x.observed === undefined || finite(x.observed)) && (x.reference === undefined || finite(x.reference));
}
export function validLifecycle(x: unknown): x is Lifecycle {
  return object(x) && Object.keys(x).every(k => ['state', 'reason', 'visibility', 'uptimeMs'].includes(k)) && ['start', 'pause', 'resume', 'heartbeat', 'close'].includes(String(x.state)) &&
    (x.reason === undefined || ['user', 'background', 'error', 'restore', 'new_city', 'unknown'].includes(String(x.reason))) &&
    (x.visibility === undefined || ['visible', 'hidden'].includes(String(x.visibility))) && (x.uptimeMs === undefined || (finite(x.uptimeMs) && x.uptimeMs >= 0 && x.uptimeMs <= 31536000000));
}
type Point = { day: number; pop?: number; employed?: number; price?: number; homeless?: number; treasury?: number };
/** 经济诊断多久要一份:每个游戏年一份;出了异常再补两三份;两份之间至少隔两分钟(原来每 30 天、隔两秒,加上一直有欠薪就一直补,一个人一天传了十几兆)。 */
export const DIAGNOSTIC_PACE = { periodDays: 360, gapMs: 120_000, codeGapMs: 600_000, minDays: 3 };
export function createDiagnosticSampler(now: () => number = Date.now, pace = DIAGNOSTIC_PACE) {
  let lastDay = -1, lastAt = -Infinity, lastPeriodic = -1, followups = 0, followupDay = -1;
  const points: Point[] = [], incidentAt = new Map<IncidentCode, number>();
  return {
    burst(day = points.at(-1)?.day ?? lastDay) { followups = Math.max(followups, 2); followupDay = Math.max(followupDay, day); },
    reset() { lastDay = lastPeriodic = followupDay = -1; lastAt = -Infinity; followups = 0; points.length = 0; incidentAt.clear(); },
    /** Response arrival is after producer execution. Requests/rejections never spend sample credit. */
    confirm(day: number) {
      if (day <= lastDay) return false;
      lastDay = day; lastAt = now();
      if (lastPeriodic < 0 || day - lastPeriodic >= pace.periodDays) lastPeriodic = day;
      if (followups > 0 && day >= followupDay) followups--;
      return true;
    },
    observe(day: number, stats: Record<string, unknown>, canRequest = true) {
      const incidents: Incident[] = [], time = now();
      const p: Point = { day };
      for (const key of ['pop', 'employed', 'price', 'homeless', 'treasury'] as const) if (finite(stats[key])) p[key] = stats[key];
      if (points.at(-1)?.day !== day) {
        const previous = points.find(x => day - x.day <= 10 && day > x.day) ?? points.at(-1);
        const compare = (code: IncidentCode, current: number | undefined, before: number | undefined, bad: (a: number, b: number) => boolean) => {
          if (current === undefined || before === undefined || !bad(current, before) || time - (incidentAt.get(code) ?? -Infinity) < pace.codeGapMs) return;
          incidentAt.set(code, time); incidents.push({ code, severity: 'warning', phase: 'step', count: 1, observed: current, reference: before });
        };
        if (previous && previous.day < day && day - previous.day <= 30) {
          compare('population_drop', p.pop, previous.pop, (a, b) => b > 0 && b - a >= 3 && a <= b * .95);
          compare('employment_drop', p.employed, previous.employed, (a, b) => b > 0 && b - a >= 3 && a <= b * .8);
          compare('price_jump', p.price, previous.price, (a, b) => b > 0 && (a >= b * 1.5 || a <= b * .5));
          compare('homeless_jump', p.homeless, previous.homeless, (a, b) => a - b >= 5);
          compare('treasury_drop', p.treasury, previous.treasury, (a, b) => b > 1000 && a <= b * .5);
        }
        points.push(p); while (points.length > 64 || (points[0] && day - points[0].day > 30)) points.shift();
      }
      if (incidents.length) { followups = Math.max(followups, 3); followupDay = Math.max(followupDay, day); }
      const periodic = lastPeriodic < 0 || day - lastPeriodic >= pace.periodDays;
      const dayEligible = lastDay < 0 || day - lastDay >= pace.minDays;
      const wait = Math.max(0, pace.gapMs - (time - lastAt));
      const due = periodic || followups > 0;
      const request = canRequest && dayEligible && wait === 0 && due;
      return { request, incidents, due: dayEligible && due, delayMs: dayEligible && due && wait > 0 ? Math.ceil(wait) : null, reason: incidents[0]?.code ?? (periodic ? 'periodic' : 'incident_followup') };
    },
  };
}

/** No text, stack, URL, event target or input is retained. Draining preserves early errors until a city exists. */
export function createEarlyIssueBuffer(limit = 32) {
  const pending: Incident[] = []; let dropped = 0;
  return {
    push(issue: Incident) {
      const prior = pending.find(x => x.code === issue.code && x.phase === issue.phase);
      if (prior) { prior.count = Math.min(0xffffffff, prior.count + issue.count); return; }
      if (pending.length >= limit) { dropped++; return; }
      pending.push({ ...issue });
    },
    drain() { const result = pending.splice(0).map(x => ({ ...x })); return { issues: result, dropped }; },
    restore(issues: Incident[]) { for (const issue of issues) this.push(issue); },
  };
}

export const earlyGameIssues = createEarlyIssueBuffer();

/** 出错:带消息和调用栈(网址的查询串和 # 后面的去掉,本站网址缩成路径);同一种错按指纹合并,第一次带调用栈,之后只报又出了几次。 */
export const FAULT_KINDS = ['js', 'promise', 'worker', 'engine', 'panic', 'oom', 'storage', 'save', 'resource', 'unclean', 'stall'] as const;
export type FaultKind = typeof FAULT_KINDS[number];
export type Fault = { kind: FaultKind; fp: string; message: string; stack?: string; where?: string; mem?: number; count?: number; repeat?: boolean };
export function cleanText(value: unknown, limit: number): string {
  let s = typeof value === 'string' ? value : value == null ? '' : String(value);
  try { if (typeof location !== 'undefined' && location.origin && location.origin !== 'null') s = s.split(location.origin).join(''); } catch { /* 没有 location 照原样 */ }
  s = s.replace(/\?[^\s:)'"]*/g, '').replace(/(\.(?:js|mjs|wasm|html|css))#[^\s:)'"]*/g, '$1');
  s = s.replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, ' ').trim();
  return s.length > limit ? s.slice(0, limit - 1) + '…' : s;
}
const fnv = (text: string, seed: number) => { let h = seed >>> 0; for (let i = 0; i < text.length; i++) { h ^= text.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h; };
export function faultOf(kind: FaultKind, message: unknown, stack?: unknown, extra: { where?: string; mem?: number } = {}): Fault {
  const text = cleanText(message, 600) || '(没有消息)', trace = stack == null ? '' : cleanText(String(stack).split('\n').slice(0, 14).join('\n'), 3000);
  // 指纹不看数字和行列号:同一处错换了个数、换了个版本还算一种
  const frames = trace.split('\n').filter(line => /\bat\b|@|wasm-function/.test(line)).slice(0, 3).map(line => line.replace(/:\d+(:\d+)?/g, '').replace(/\d+/g, '#')).join('|');
  const key = `${kind}|${text.replace(/\d+/g, '#').slice(0, 200)}|${frames}`;
  const fp = fnv(key, 2166136261).toString(16).padStart(8, '0') + fnv(key, 0x5bd1e995).toString(16).padStart(8, '0');
  const mem = extra.mem;
  return { kind, fp, message: text, ...(trace && trace !== text ? { stack: trace } : {}), ...(extra.where && /^[a-z_]{1,24}$/.test(extra.where) ? { where: extra.where } : {}),
    ...(typeof mem === 'number' && Number.isFinite(mem) && mem >= 0 ? { mem } : {}) };
}
export function createFaultBuffer(limit = 40) {
  const seen = new Map<string, { fault: Fault; sent: boolean; pending: number }>(); let dropped = 0, listener: (() => void) | null = null;
  return {
    push(fault: Fault) {
      const prior = seen.get(fault.fp);
      if (prior) prior.pending = Math.min(0xffffffff, prior.pending + 1);
      else if (seen.size >= limit) { dropped++; return; }
      else seen.set(fault.fp, { fault, sent: false, pending: 1 });
      try { listener?.(); } catch { /* 报错的路上不能再出错 */ }
    },
    /** 要传的:新的带全文,传过的只带又出了几次。 */
    drain(): Fault[] {
      const out: Fault[] = [];
      for (const item of seen.values()) {
        if (!item.pending) continue;
        out.push(item.sent ? { kind: item.fault.kind, fp: item.fault.fp, message: item.fault.message, count: item.pending, repeat: true } : { ...item.fault, count: item.pending });
        item.sent = true; item.pending = 0;
      }
      return out;
    },
    listen(fn: (() => void) | null) { listener = fn; },
    get dropped() { return dropped; },
  };
}
export const earlyFaults = createFaultBuffer();
// Importing this module in Node is inert. In the app it runs before sandbox main()/World creation.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('error', event => {
    const resource = event.target && event.target !== window;
    if (resource) {
      const target = event.target as { src?: string; href?: string };
      let url: URL; try { url = new URL(target.src ?? target.href ?? '', location.href); if (url.origin !== location.origin) return; } catch { return; }
      earlyFaults.push(faultOf('resource', `没下来:${url.pathname}`, undefined, { where: 'startup' })); return;
    }
    const e = event as ErrorEvent, error = e.error as { stack?: unknown } | undefined;
    earlyFaults.push(faultOf('js', e.message || String(error ?? '出错'), error?.stack ?? `${e.filename ?? ''}:${e.lineno ?? 0}:${e.colno ?? 0}`));
  }, true);
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason as { message?: unknown; stack?: unknown } | undefined;
    const object = !!reason && typeof reason === 'object';
    earlyFaults.push(faultOf('promise', object && 'message' in reason! ? reason!.message : String(reason), object ? reason!.stack : undefined));
  });
}
